import React from 'react';

import { ComponentDesign, Member, ParallelLine } from '@paragon/contract-library';
import { Point2D } from '@paragon/geometry-library';
import { calculateLine, findLine } from '@paragon/relative-geometry';
import { RenderOrder } from '@paragon/three-shared';
import { distance2D } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { getMemberLine } from '../../store/actions/insertActions';
import {
  calculateComponentBounds,
  getActiveDimensionBounds,
  getFontSizeFromComponentBoundingBox,
} from '../../store/selectors/boundsSelectors';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getDistanceFormatterSelector } from '../../store/selectors/getDistanceFormatterSelector';
import { getSingleSelectedItemEntryOrNull } from '../../store/selectors/getSelectedItemSelector';
import { setReferenceLines } from '../../store/slices/componentDesignsSlice';
import { AngleDimension } from './AngleDimension';
import { DistanceDimension } from './DistanceDimension';
import { EditableDistanceDimension } from './EditableDistanceDimension';
import { getAngleDimensionsForMember } from './getAngleDimensionsForMember';
import getDistanceDimensionDefinitionsForMember from './getDistanceDimensionDefinitionsForMember';
import { ParallelLineDimension } from './lines/ParallelLineDimensions';

export function SelectedMemberDimensions() {
  const componentDesign = useAppSelector(getActiveComponentDesign);
  const selectedItem = useAppSelector(getSingleSelectedItemEntryOrNull);
  const showSelectedMemberCutAngles = useAppSelector((state) => state.visualOptions.showSelectedMemberCutAngles);
  const showSelectedMemberLengthDimensions = useAppSelector(
    (state) => state.visualOptions.showSelectedMemberLengthDimensions,
  );

  if (!selectedItem || (selectedItem.type !== 'member' && selectedItem.type !== 'brace')) return null;
  const selectedMember = selectedItem.item;
  if (!componentDesign) throw new Error('Could not find active component design while member was selected');

  const definitionLine = findLine(selectedMember.relativeGeometry?.lineGuid ?? null, componentDesign.component);
  if (!definitionLine) return null;

  const scabDimensionProps = getScabDimensionPropsOrNull(componentDesign, selectedMember);

  return (
    <>
      {showSelectedMemberCutAngles && <MemberAngleDimensions member={selectedMember} />}
      {showSelectedMemberLengthDimensions && !scabDimensionProps && (
        <MemberDistanceDimensions member={selectedMember} />
      )}
      {definitionLine.lineType === 'Parallel' && <ParallelLineDimension line={definitionLine} />}
      {scabDimensionProps && <ScabLengthDimension {...scabDimensionProps} />}
    </>
  );
}

function MemberAngleDimensions({ member }: { member: Member }) {
  const component = useAppSelector(getActiveComponentDesign)?.component;
  if (!component) return null;
  return getAngleDimensionsForMember(member, component).map((props, index) => (
    <AngleDimension {...props} key={index} />
  ));
}

function MemberDistanceDimensions({ member }: { member: Member }) {
  const component = useAppSelector(getActiveComponentDesign)?.component;
  const dimensionBounds = useAppSelector(getActiveDimensionBounds);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);
  if (!component) return null;

  const componentBoundingBox = calculateComponentBounds(component, false, dimensionBounds);
  const fontSize = getFontSizeFromComponentBoundingBox(componentBoundingBox);

  const distanceDimensionDefinitions = getDistanceDimensionDefinitionsForMember(member, component);
  return (
    <>
      {distanceDimensionDefinitions.map(({ start, end, textFloat }, index) => (
        <DistanceDimension
          key={index}
          segment={{ start, end }}
          showEndpointMarks
          textFloat={textFloat}
          distanceFormatter={distanceFormatter}
          labelData={{ firstNumber: distance2D(start.x, start.y, end.x, end.y), fontSize }}
          labelRenderOrder={RenderOrder.GlyphVisual_MainDimension}
        />
      ))}
    </>
  );
}

interface ScabLengthDimensionProps {
  basePoint: Point2D;
  endPoint: Point2D;
  totalOffset: number;
  greaterLine: ParallelLine;
  lesserLine: ParallelLine;
  componentDesignGuid: string;
}

// This component assumes scabs/braces are defined by a pair of parallel lines cutting the member into a rectangular
// shape. If brace insertion code changes, this code may need to change too.
function ScabLengthDimension(props: ScabLengthDimensionProps) {
  const { basePoint, endPoint, totalOffset, greaterLine, lesserLine, componentDesignGuid } = props;
  const dispatch = useAppDispatch();
  const onChange = (distance: number) => {
    const halfDelta = (distance - totalOffset) / 2;
    const newGreaterLine = { ...greaterLine, offset: greaterLine.offset + halfDelta };
    const newLesserLine = { ...lesserLine, offset: lesserLine.offset - halfDelta };
    dispatch(setReferenceLines({ referenceLines: [newGreaterLine, newLesserLine], componentDesignGuid }));
  };

  return (
    <EditableDistanceDimension
      start={basePoint}
      end={endPoint}
      distance={totalOffset}
      showEndpointMarks
      onChange={onChange}
    />
  );
}

function getScabDimensionPropsOrNull(componentDesign: ComponentDesign, scab: Member): ScabLengthDimensionProps | null {
  if (!componentDesign || !scab.relativeGeometry?.isBrace) return null;
  const { component } = componentDesign;
  const { baseCutGuids, endCutGuids } = scab.relativeGeometry;
  if (baseCutGuids.length !== 1 || endCutGuids.length !== 1) return null;
  const [baseCutGuid, endCutGuid] = [baseCutGuids[0], endCutGuids[0]];
  const [baseLine, endLine] = [baseCutGuid, endCutGuid].map((guid) => findLine(guid, component));
  if (baseLine?.lineType !== 'Parallel' || endLine?.lineType !== 'Parallel' || baseLine.lineGuid !== endLine.lineGuid)
    return null;

  const [baseLineGeometry, endLineGeometry] = [baseLine, endLine].map((line) => calculateLine(line, component));
  const centerLine = getMemberLine(component.referenceLines ?? [], scab.relativeGeometry.guid, 'Center');
  const centerLineGeometry = centerLine && calculateLine(centerLine, component);
  if (!baseLineGeometry || !endLineGeometry || !centerLineGeometry) return null;

  const directionToOffsetDimension = baseLineGeometry.direction.reverse();
  const [unshiftedBasePoint, unshiftedEndPoint] = [baseLineGeometry, endLineGeometry].map((line) =>
    centerLineGeometry.intersectWithLine(line),
  );
  if (!unshiftedBasePoint || !unshiftedEndPoint) return null;
  const [basePoint, endPoint] = [unshiftedBasePoint, unshiftedEndPoint].map((point) =>
    point.translate(directionToOffsetDimension.toVector(12)),
  );

  const [greaterLine, lesserLine] = baseLine.offset > endLine.offset ? [baseLine, endLine] : [endLine, baseLine];
  const totalOffset = greaterLine.offset - lesserLine.offset;

  return { basePoint, endPoint, totalOffset, greaterLine, lesserLine, componentDesignGuid: componentDesign.guid };
}
